import { useEffect, useState } from 'react';
import * as React from 'react';
import {
    Button,
    Col,
    Input,
    InputNumber,
    Radio,
    RadioChangeEvent,
    Row,
    Select,
    Tooltip,
} from 'antd';
import {
    MinusCircleOutlined,
    NotificationOutlined,
    PlusOutlined,
    QuestionCircleFilled,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import { formRules, getDefaultLayout } from 'constants/GlobalConfig';
import BaseModal from 'components/modules/BaseModal';
import BaseForm from 'components/modules/BaseForm';
import { checkFormChange } from 'utils/Helper';
import { Explanation } from './QuestionItem/Explanation';
const Form = BaseForm.ModalForm;
const layout = getDefaultLayout();
const { Option } = Select;

const INIT_FORM_VALUE: EverydayNote.Form.Question = {
    candidates: [
        { content: '', order: 0 },
        { content: '', order: 1 },
    ],
    content: '',
    detail: '',
    isRequired: true,
    type: 1,
    markDetails: [],
};

type Props = {
    isCopy: boolean
    loading?: boolean
    onCancel: () => void
    onOk: (question: EverydayNote.Form.Question) => void
    question?: EverydayNote.Form.Question
    questionsCount: number
    visible: boolean
    isAnswered: boolean
};


const QuestionModal: React.FC<Props> = ({
    isCopy,
    loading = false,
    onCancel,
    onOk,
    question,
    questionsCount,
    visible,
    isAnswered = false,
}) => {
    const [form] = Form.useForm<EverydayNote.Form.Question>();
    const [formInitValue, setFormInitValue] = useState<any>({});
    const [formChangeValue, setFormChangeValue] = useState<any>({});
    const [isDisabledSubmitBtn, setIsDisabledSubmitBtn] = useState(false);

    const isCreate = !question;
    const isUpdate = question && !isCopy;

    const type = Form.useWatch('type', form) ?? INIT_FORM_VALUE.type;
    const evaluationScaleMax = Form.useWatch('evaluationScaleMax', form);

    useEffect(() => {
        if (!visible) return;

        form.resetFields();

        if (isCreate) {
            form.setFieldsValue({
                ...INIT_FORM_VALUE,
                content: `${questionsCount + 1}. `,
            });
        } else {
            form.setFieldsValue({
                ...question!,
            });
            if (isUpdate) {
                setFormInitValue(question);
                setIsDisabledSubmitBtn(true);
            }
        }
    }, [form, isCopy, isCreate, question, questionsCount, visible]);

    useEffect(() => {
        if (isUpdate) {
            const isFormChange = checkFormChange(formInitValue, formChangeValue);
            setIsDisabledSubmitBtn(!isFormChange);
        }
    }, [formChangeValue]);

    const handleChangeType = (e: RadioChangeEvent) => {
        const type = e.target.value as EverydayNote.Form.Question['type'];

        switch (type) {
            case 1: // 単一選択
            case 2: // 複数選択
            case 7: // プルダウン
                form.setFieldsValue({
                    candidates: [
                        { content: '', order: 0 },
                        { content: '', order: 1 },
                    ],
                });
                break;
            case 10: // 段階選択式
                form.setFieldsValue({
                    candidates: [
                        { content: '', order: 0 },
                        { content: '', order: 1 },
                    ],
                    // 序列設定の既定値は「選択肢1が良い回答」
                    candidateOrderPositive: true,
                });
                break;
            case 3: // 自由記述
            case 5: // 教材
                break;
            case 4: // 顔マーク5段階
                form.setFieldsValue({
                    markDetails: Array(5).fill(''),
                });
                break;
            case 6: // 星マーク
                form.setFieldsValue({
                    evaluationScaleMax: undefined,
                    markDetails: Array(10).fill(''),
                });
                break;
            case 8: // スライダー
                form.setFieldsValue({
                    showSliderValueDetails: true,
                    sliderInitialUserValue: undefined,
                    sliderRangeMax: undefined,
                    sliderRangeMin: undefined,
                    sliderInitialUserValueDetail: '',
                    sliderRangeMaxDetail: '',
                    sliderRangeMinDetail: '',
                });
                break;
            case 9: // 天気マーク
                form.setFieldsValue({
                    markDetails: Array(10).fill(''),
                });
                break;
            default:
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _: never = type;
                break;
        }
        if (isUpdate) {
            handleValuesChange();
        }
    };


    const onFinish = (values: EverydayNote.Form.Question) => {
        const { candidates = [], ...resetVales } = removeExtraFields(values);
        const candidatesWithOrder = candidates.map((candidate, i) => ({
            ...candidate,
            order: i,
        }));

        onOk({
            ...question,
            ...resetVales,
            candidates: candidatesWithOrder,
            order: question?.order ?? questionsCount,
        });
        setIsDisabledSubmitBtn(false);
    };

    const handleCancel = () => {
        onCancel();
        if (isUpdate) {
            setIsDisabledSubmitBtn(false);
        }
    };

    const handleValuesChange = () => {
        if (isUpdate) {
            setFormChangeValue({
                ...form.getFieldsValue(true)
            });
        }
    };

    return (
        <BaseModal
            className='common-modal everyday-note-modal'
            title={<span><NotificationOutlined className='icon-r' /> {isCreate ? ' 設問の新規作成' : '設問の編集'}</span>}
            visible={visible}
            onCancel={handleCancel}
            footer={[
                <Button
                    key='cancel'
                    size='large'
                    loading={loading}
                    disabled={loading}
                    onClick={handleCancel}
                >
                    キャンセル
                </Button>,
                <Button
                    key='submit'
                    type='primary'
                    size='large'
                    loading={loading}
                    disabled={loading || isDisabledSubmitBtn}
                    onClick={form.submit}
                >
                    保存
                </Button>,
            ]}
            maskClosable={false}
        >
            <Form {...layout} form={form} onFinish={onFinish} 
                onValuesChange={() => handleValuesChange()}>
                <Form.Item name='uuid' hidden><input /></Form.Item>

                <Form.Item
                    name='content'
                    label='設問'
                    rules={[
                        formRules.required({ label: '設問' }),
                        formRules.range({ max: 128 }),
                    ]}
                >
                    <Input placeholder='設問を入力してください' disabled={isAnswered}/>
                </Form.Item>

                <Form.Item
                    name='detail'
                    label='設問詳細'
                    rules={[
                        formRules.range({ max: 10000 }),
                    ]}
                >
                    <Input placeholder='設問詳細を入力してください' disabled={isAnswered}/>
                </Form.Item>

                <Form.Item name='isRequired' label='必須回答'>
                    <Radio.Group disabled={isAnswered}>
                        <Radio value={true}>必須</Radio>
                        <Radio value={false}>任意</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name='type' label='回答形式'>
                    <Radio.Group onChange={handleChangeType} disabled={isAnswered}>
                        <Row gutter={[8, 8]} style={{ whiteSpace: 'pre' }}>
                            <Col lg={14} md={24} xs={24}>
                                <Row>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={1}>単一選択式</Radio>
                                    </Col>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={2}>複数選択式</Radio>
                                    </Col>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={3}>自由記述式</Radio>
                                    </Col>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={4}>顔マーク5段階</Radio>
                                    </Col>
                                </Row>
                            </Col>
                            <Col lg={14} md={24} xs={24}>
                                <Row>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={6}>星マーク</Radio>
                                    </Col>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={7}>プルダウン</Radio>
                                    </Col>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={8}>スライダー</Radio>
                                    </Col>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={9}>天気マーク</Radio>
                                    </Col>
                                </Row>
                            </Col>
                            <Col lg={14} md={24} xs={24}>
                                <Row>
                                    <Col md={6} sm={12} xs={12}>
                                        <Radio value={10}>段階選択式</Radio>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Radio.Group>
                </Form.Item>

                {(type === 1 || type === 2 || type === 7 || type === 10) && (
                    <Form.List name='candidates'>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={`選択肢 ${index + 1}`}
                                        required
                                        style={{ marginBottom: 0 }}
                                        key={field.key}
                                    >
                                        <Form.Item name={[field.name, 'uuid']} hidden><input /></Form.Item>

                                        <Row gutter={[16, 12]} >
                                            <Col span={22}>
                                                <Form.Item
                                                    name={[field.name, 'content']}
                                                    rules={[
                                                        formRules.required({ label: `選択肢${index + 1}の名称` }),
                                                        formRules.range({ max: 255 }),
                                                    ]}
                                                >
                                                    <Input placeholder={`選択肢${index + 1}の名称を入力してください`} />
                                                </Form.Item>
                                            </Col>
                                            {(index > 1) && (
                                                <Col span={2}>
                                                    <Button
                                                        type='text'
                                                        onClick={() => remove(index)}
                                                    >
                                                        <MinusCircleOutlined style={{ fontSize: '24px' }} />
                                                    </Button>
                                                </Col>
                                            )}
                                        </Row>
                                    </Form.Item>
                                ))}

                                <Form.Item wrapperCol={{ lg: { span: 24, offset: 0 }, xl: { span: 20, offset: 4 } }}>
                                    <Button
                                        type='dashed'
                                        style={{ width: '100%' }}
                                        // add をそのまま渡すと, 以下のエラーが発生する.
                                        // This synthetic event is reused for performance reasons.
                                        onClick={() => add()}
                                    >
                                        <PlusOutlined />
                                        選択肢の追加
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                )}

                {type === 10 && (
                    <Form.Item
                        name='candidateOrderPositive'
                        label={
                            <span>
                                選択肢の序列設定
                                &nbsp;&nbsp;
                                <Tooltip title='回答画面には選択肢1を先頭として順番に表示されます。選択肢1に指定した内容が、良い回答または悪い回答なのかを「選択肢の序列設定」で指定してください。'>
                                    <QuestionCircleOutlined/>
                                </Tooltip>
                            </span>
                        }
                    >
                        <Radio.Group disabled={isAnswered}>
                            <Radio value={true} style={{ display: 'block', whiteSpace: 'normal' }}>
                                選択肢1の方が良い回答とする（最後の選択肢になるにつれ悪い回答とする）
                            </Radio>
                            <Radio value={false} style={{ display: 'block', whiteSpace: 'normal' }}>
                                選択肢1の方が悪い回答とする（最後の選択肢になるにつれ良い回答とする）
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                )}

                {type === 6 && (
                    <Form.Item
                        name='evaluationScaleMax'
                        label='段階数'
                        rules={[formRules.required({ label: '段階数', isSelect: true }),]}
                    >
                        <Select disabled={isAnswered}>
                            {[...Array(8)].map((_, i) => (
                                <Option key={i + 3} value={i + 3}>{i + 3}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                {type === 8 && (
                    <Row>
                        <Col span={24}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Form.Item
                                        name='sliderRangeMin'
                                        label='最小値'
                                        rules={[
                                            formRules.required({ label: 'スライダーの最小値' }),
                                        ]}
                                        labelCol={{ span: 8 }}
                                    >
                                        <InputNumber
                                            precision={0}
                                            placeholder='スライダーの最小値を設定してください'
                                            style={{ width: '100%' }}
                                            controls={false}
                                            max={100}
                                            min={0}
                                            disabled={isAnswered}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name='sliderRangeMinDetail'
                                        label={
                                            <span>
                                                値の説明
                                                &nbsp;&nbsp;
                                                <Tooltip title='回答時に表示されます。'>
                                                    <QuestionCircleOutlined/>
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[formRules.range({ max: 20 })]}
                                        labelCol={{ md: 8, sm: 10, xs: 12 }}
                                    >
                                        <Input placeholder='例）つらい'/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Form.Item
                                        name='sliderRangeMax'
                                        label='最大値'
                                        labelCol={{ span: 8 }}
                                        dependencies={['sliderRangeMin', 'sliderInitialUserValue']}
                                        rules={[
                                            formRules.required({ label: 'スライダーの最大値' }),
                                            {
                                                validator: async (_, value) => {
                                                    const { sliderInitialUserValue, sliderRangeMin } = form.getFieldsValue();
                                                    if (sliderInitialUserValue === undefined && sliderRangeMin === undefined) {
                                                        return Promise.resolve();
                                                    } else if (Number.isFinite(value)) {
                                                        if (sliderRangeMin !== undefined && sliderRangeMin !== null && sliderRangeMin >= value) {
                                                            return Promise.reject(new Error(`「最大値」の値は「最小値」の値より大きくなければなりません。`));
                                                        } else if (sliderInitialUserValue !== undefined && sliderInitialUserValue !== null && sliderInitialUserValue > value) {
                                                            return Promise.reject(new Error(`「最大値」の値は「初期値」の値以上でなければなりません。`));
                                                        }
                                                    }
                                                }
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            precision={0}
                                            placeholder='スライダーの最大値を設定してください'
                                            style={{ width: '100%' }}
                                            controls={false}
                                            max={100}
                                            min={0}
                                            disabled={isAnswered}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name='sliderRangeMaxDetail'
                                        label={
                                            <span>
                                                値の説明
                                                &nbsp;&nbsp;
                                                <Tooltip title='回答時に表示されます。'>
                                                    <QuestionCircleOutlined/>
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[formRules.range({ max: 20 })]}
                                        labelCol={{ md: 8, sm: 10, xs: 12 }}
                                    >
                                        <Input placeholder='例）げんき'/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Form.Item
                                        name='sliderInitialUserValue'
                                        label='初期値'
                                        labelCol={{ span: 8 }}
                                        dependencies={['sliderRangeMax', 'sliderRangeMin']}
                                        rules={[
                                            formRules.required({ label: 'スライダーの初期値' }),
                                            {
                                                validator: async (_, value) => {
                                                    const { sliderRangeMin, sliderRangeMax } = form.getFieldsValue();
                                                    if (sliderRangeMin === undefined && sliderRangeMax === undefined) {
                                                        return Promise.resolve();
                                                    } else if (Number.isFinite(value)) {
                                                        if (sliderRangeMin !== undefined && sliderRangeMin !== null && sliderRangeMin > value) {
                                                            return Promise.reject(new Error(`「初期値」の値は「最小値」の値以上でなければなりません。`));
                                                        } else if (sliderRangeMax !== undefined && sliderRangeMax !== null && sliderRangeMax < value) {
                                                            return Promise.reject(new Error(`「初期値」の値は「最大値」の値以下でなければなりません。`));
                                                        }
                                                    }
                                                }
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            precision={0}
                                            placeholder='スライダーの初期値を設定してください'
                                            style={{ width: '100%' }}
                                            controls={false}
                                            max={100}
                                            min={0}
                                            disabled={isAnswered}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name='sliderInitialUserValueDetail'
                                        label={
                                            <span>
                                                値の説明
                                                &nbsp;&nbsp;
                                                <Tooltip title='回答時に表示されます。'>
                                                    <QuestionCircleOutlined/>
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[formRules.range({ max: 20 })]}
                                        labelCol={{ md: 8, sm: 10, xs: 12 }}
                                    >
                                        <Input placeholder='例）ふつう'/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Form.Item 
                                name='showSliderValueDetails' 
                                label={
                                    <span>
                                        値の表示
                                        &nbsp;&nbsp;
                                        <Tooltip title='スライダー回答時に、設定した最小値・最大値・初期値を回答画面に表示するか選択します。'>
                                            <QuestionCircleOutlined/>
                                        </Tooltip>
                                    </span>
                                }
                                labelCol={{ span: 4 }}
                            >
                                <Radio.Group disabled={isAnswered}>
                                    <Radio value={true}>する</Radio>
                                    <Radio value={false}>しない</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {(type === 4 || type === 6 || type === 9) && (
                    <Form.List name='markDetails'>
                        {(fields) => (
                            <Form.Item label={
                                <span>
                                    マークの説明
                                    &nbsp;&nbsp;
                                    <Tooltip title='マークの説明は回答時に表示されます。'>
                                        <QuestionCircleFilled style={{ fontSize: '1rem', color: '#ccc' }}/>
                                    </Tooltip>
                                </span>
                            }>
                                <Explanation type={type} evaluationScaleMax={evaluationScaleMax} />
                            </Form.Item>
                        )}
                    </Form.List>
                )}
            </Form>
        </BaseModal>
    );
};

export default QuestionModal;


const removeExtraFields = (values: EverydayNote.Form.Question) => {
    const blankSlider = {
        sliderRangeMax: undefined,
        sliderRangeMin: undefined,
        sliderInitialUserValue: undefined,
        sliderInitialUserValueDetail: '',
        sliderRangeMaxDetail: '',
        sliderRangeMinDetail: '',
    };

    const blankCandidates = { candidates: [] };

    const blankStarMark = {
        evaluationScaleMax: undefined,
        markDetails: [],
    }

    switch (values.type) {
        case 1: // 単一選択
        case 2: // 複数選択
        case 7: // プルダウン
            return {
                ...values,
                ...blankSlider,
                ...blankStarMark,
            };
        case 10: // 段階選択式（順序尺度）
            return {
                ...values,
                ...blankSlider,
                ...blankStarMark,
            };
        case 3: // 自由記述
        case 5: // 教材
            return {
                ...values,
                ...blankSlider,
                ...blankStarMark,
                ...blankCandidates,
            };
        case 4: // 顔マーク5段階
            return {
                ...values,
                ...blankCandidates,
                ...blankSlider,
                evaluationScaleMax: undefined,
                // 星の数に応じて配列をトリミングし、undefined を空に変換する。
                markDetails: values.markDetails ? values.markDetails.map((detail) => detail ?? '') : Array(5).fill(''),
            };
        case 6: // 星マーク
            return {
                ...values,
                ...blankCandidates,
                ...blankSlider,
                // 星の数に応じて配列をトリミングし、undefined を空に変換する。
                markDetails: values.markDetails?.slice(0, values.evaluationScaleMax).map((detail) => detail ?? ''),
            };
        case 8: // スライダー
            return {
                ...values,
                ...blankCandidates,
                ...blankStarMark,
            };
        case 9: // 天気マーク
            return {
                ...values,
                ...blankCandidates,
                ...blankSlider,
                evaluationScaleMax: undefined,
                // 星の数に応じて配列をトリミングし、undefined を空に変換する。
                markDetails: values.markDetails
                    ? values.markDetails
                          .map((detail) => detail ?? '')
                          .concat(Array(10 - values.markDetails.length).fill(''))
                    : Array(10).fill(''),
            };
        default:
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _: never = values.type;
            return values;
    }
};
